import { ChangeDetectorRef, Component, Input, OnInit } from '@angular/core';
import { ControlContainer, NgForm } from '@angular/forms';
import { SessionService } from '@monsido/core/session/session.service';
import { TranslateService } from '@client/app/services/translate/translate.service';
import {
    AccessibilitySourceCodeExcludeRepoService,
} from '@client/app/services/api/accessibility-source-code-exclude-repo.service';
import {
    SourceCodeExcludesApiService,
} from '@client/app/pages/admin/source-code-excludes/services/source-code-excludes-api.service';
import { ActiveFeatureService } from '@monsido/services/active-feature/active-feature.service';
import { MonEventService } from '@monsido/services/mon-event/mon-event.service';
import {
    DefaultConstraintsAndExcludesService,
} from '@client/app/forms/domain/default-constraints-and-excludes-service/default-constraints-and-excludes.service';
import { GuidelineInfoService } from '@monsido/modules/accessibility/services/guideline-info/guideline-info.service';
import { PipeService } from '@client/app/filters/pipe-transformation.service';
import { uniqueBy } from '@client/app/shared/utils/object-utils';
import { MON_EVENTS } from '@monsido/core/constants/mon-events.constant';
import { SCE_MODULES } from '@client/app/pages/admin/source-code-excludes/constants/sce_modules';
import {
    DomainDataPrivacySettingsComponent,
} from '@client/app/modules/data-privacy/components/domain-settings/domain-data-privacy-settings.component';
import {
    PageFixFormDomainSettingsComponent,
} from '@client/app/modules/page-fix/forms/domain-settings/domain-settings.component';

@Component({
    selector: 'form-domain-features',
    templateUrl: './form-domain-features.component.html',
    viewProviders: [{ provide: ControlContainer, useExisting: NgForm }],
    standalone: false,
})
export class FormDomainFeaturesComponent implements OnInit {
    @Input() domain: any = {};
    @Input() form?: NgForm;

    featureAccessibilityGuidelineId = '';
    selectedWcagAccessibilityVersion: number | null = null;
    selectedQualwebAccessibilityVersion: number | null = null;
    isWcag = false;
    sourceCodeExcludes: any[] = [];
    domainExcludes: any[] = [];
    customer: any = {};
    guidelineMapEntries: any[] = [];
    accessibilities: any[] = [];
    readabilities: any[] = [];
    showAutofix = false;
    showHeatmaps = false;
    loadingProfiles = false;
    showDataPrivacy = false;
    promiseSourceCodeExcludes: Promise<any> | null = null;

    constructor (
        private sessionService: SessionService,
        private translateService: TranslateService,
        private accessibilitySourceCodeExcludeRepo: AccessibilitySourceCodeExcludeRepoService,
        private sourceCodeExcludesApiService: SourceCodeExcludesApiService,
        private activeFeatureService: ActiveFeatureService,
        private monEventsService: MonEventService,
        private defaultConstraintsAndExcludesService: DefaultConstraintsAndExcludesService,
        private guidelineInfoService: GuidelineInfoService,
        private pipeService: PipeService,
        private cdRef: ChangeDetectorRef,
    ) {}

    ngOnInit (): void {
        this.activate();
    }

    private activate (): void {
        this.guidelineMapEntries = Object.entries({
            ...this.guidelineInfoService.wcag,
            ...this.guidelineInfoService.qw,
        });
        this.sourceCodeExcludes = [];
        this.promiseSourceCodeExcludes = null;
        this.domainExcludes = [];
        this.customer = this.sessionService.customer;

        if (typeof this.domain.features.statistics !== 'boolean') {
            this.domain.features.statistics = this.customer.settings.statistics;
        }

        this.domain.features.accessibility = this.domain.features.accessibility || this.customer.settings.accessibility;
        this.featureAccessibilityGuidelineId = (this.domain.features.accessibility || '').split('_v')[0];
        this.showAutofix = false;
        this.showHeatmaps = false;
        this.loadingProfiles = false;
        this.showDataPrivacy = false;

        this.setupAccessibilityOptions(this.domain.features.accessibility);
        this.setupReadabilityOptions();
        this.setupPlanSpecificFeature();
        this.setupPdfAccessibilityGuideline();

        this.defaultConstraintsAndExcludesService.defaultCms$.subscribe((cms: any) => {
            if (!cms) {
                return;
            }
            this.domainExcludes = uniqueBy(
                [...this.domainExcludes, ...this.sourceCodeExcludes.filter((sce) => sce.default_for_cms === cms)],
                'id',
            );
        });

        this.isWcag = this.isWcagGuideline();
        this.setAccessibilityErrorsOnly();
    }

    onGuidelineChange (): void {
        this.setAccessibilityErrorsOnly();

        // If a guideline is selected
        if (this.featureAccessibilityGuidelineId) {
            this.domain.features.accessibility = this.featureAccessibilityGuidelineId;
            return;
        }

        this.domain.features.accessibility = undefined;
    }

    private setAccessibilityErrorsOnly (): void {
        if (!this.isWcagGuideline()) {
            this.domain.features.accessibility_errors_only = false;
        }
    }

    private isWcagGuideline (): boolean {
        return !!this.guidelineInfoService.wcag[this.featureAccessibilityGuidelineId];
    }

    openDataPrivacy (): void {
        const params = {
            body: DomainDataPrivacySettingsComponent,
            size: 'md',
            data: {
                domain: this.domain,
            },
        };

        const options = {
            params,
            callback: (settings: any) => {
                if (settings) {
                    this.domain.settings.data_protection = settings;
                }
            },
        };

        this.monEventsService.run(MON_EVENTS.LOAD_DIALOG, options);
    }

    openPageCorrectSettings (): void {
        const params = {
            body: PageFixFormDomainSettingsComponent,
            size: 'md',
            data: {
                domain: this.domain,
                saveToAPI: false,
            },
        };

        const options = {
            params,
            callback: (settings: any) => {
                if (settings) {
                    this.domain.settings.scripts.page_correct.admin_only = settings.admin_only;
                    this.domain.settings.scripts.page_correct.enabled_checks = settings.enabled_checks;
                    if (this.form) {
                        this.form.control.markAsDirty();
                    }
                }
            },
        };

        this.monEventsService.run(MON_EVENTS.LOAD_DIALOG, options);
    }

    getSourceCodeExcludes (): Promise<any> {
        if (this.promiseSourceCodeExcludes !== null) {
            return this.promiseSourceCodeExcludes;
        }
        const promises: Promise<any>[] = [];

        if (this.activeFeatureService.isFeatureActive('accessibility', this.domain)) {
            promises.push(this.getAccessibilitySCE());
        }

        if (
            this.activeFeatureService.isFeatureActive('admin_source_code_excludes', this.domain) &&
            this.activeFeatureService.isFeatureActive('readability', this.domain)
        ) {
            promises.push(this.getReadabilitySCE());
        }

        if (this.activeFeatureService.isFeatureActive('admin_source_code_excludes', this.domain)) {
            promises.push(this.getMisspellingSCE());
        }

        if (
            this.activeFeatureService.isFeatureActive('admin_source_code_excludes', this.domain) &&
            this.activeFeatureService.isFeatureActive('language_match', this.domain) &&
            this.domain.scan?.use_lang_attribute === true
        ) {
            promises.push(this.getLanguageMatchSCE());
        }

        this.promiseSourceCodeExcludes = Promise.all(promises).then((values) => {
            values.forEach((sceCollection) => {
                if (sceCollection) {
                    this.sourceCodeExcludes = this.sourceCodeExcludes.concat(sceCollection);
                }
            });
            this.domainExcludes = this.sourceCodeExcludes.filter((sce) => {
                if (sce.module === SCE_MODULES.accessibility) {
                    return this.domain.accessibility_source_code_excludes?.indexOf(sce.id) > -1;
                } else {
                    return this.domain.source_code_excludes?.indexOf(sce.id) > -1;
                }
            });
            // Make sure that the domain object stays updated with the sce data
            this.onSCEChange([], this.domainExcludes);
            return this.sourceCodeExcludes;
        });
        return this.promiseSourceCodeExcludes;
    }

    onSCEChange (oldVal: any[], newSCEs: any[]): void {
        this.domain.accessibility_source_code_excludes = [];
        this.domain.source_code_excludes = [];

        newSCEs?.forEach((sce) => {
            if (sce.module === SCE_MODULES.accessibility) {
                this.domain.accessibility_source_code_excludes.push(sce.id);
            } else {
                this.domain.source_code_excludes.push(sce.id);
            }
        });

        this.cdRef.detectChanges();
    }

    private setupPdfAccessibilityGuideline (): void {
        if (this.customer?.plan_traits?.clarity_pdf_accessibility) {
            this.domain.features.clarity_guideline = 'WCAG2_2';
        }
    }

    private setupAccessibilityOptions (accessibilityGuideline: any): void {
        const isMobileGuideline = this.customer?.plan_traits?.mobile_guidelines;

        const isOldGuideline = (typeof accessibilityGuideline === 'string' &&
            (accessibilityGuideline.includes('v2') || !accessibilityGuideline.includes('_v')));

        this.accessibilities = this.createGuidelineOpts(isOldGuideline, isMobileGuideline);
    }

    private setupReadabilityOptions (): void {
        this.readabilities = [
            {
                readability_test_id: 'flesch_kincaid_re',
                name: this.pipeService.get('translateReadability')('flesch_kincaid_re') + ' ' + this.translateService.getString('Readability test (for English pages only)'),
            },
            {
                readability_test_id: 'lix',
                name: this.pipeService.get('translateReadability')('lix') + ' ' + this.translateService.getString('Readability test'),
            },
        ];
    }

    private setupPlanSpecificFeature (): void {
        const customer = this.sessionService.customer;
        if (customer) {
            if (customer.plan_traits.page_fix) {
                this.showAutofix = this.pipeService.get('activeFeature')('page_fix');
            }
            if (customer.plan_traits.data_protection) {
                this.showDataPrivacy = this.pipeService.get('activeFeature')('data_privacy');
            }
        }
    }

    private getAccessibilitySCE (): Promise<any> {
        const params = {
            page_size: 0,
            include_inherited: true,
        };

        return this.accessibilitySourceCodeExcludeRepo.getAll(params).then(
            (excludes: any[]) => {
                return excludes.map((exclude) => {
                    exclude.module = SCE_MODULES.accessibility;
                    exclude.label = exclude.short_description;
                    return exclude;
                });
            },
            () => {
                return [];
            },
        );
    }

    private getReadabilitySCE (): Promise<any> {
        const params = {
            page_size: 0,
        };

        return this.sourceCodeExcludesApiService.getAll(SCE_MODULES.readability, params).then(
            (sces: any[]) => {
                return sces.map((sce) => {
                    sce.module = SCE_MODULES.readability;
                    sce.label = sce.title;
                    return sce;
                });
            },
            () => {
                return [];
            },
        );
    }

    private getMisspellingSCE (): Promise<any> {
        const params = {
            page_size: 0,
        };

        return this.sourceCodeExcludesApiService.getAll(SCE_MODULES.misspelling, params).then(
            (sces: any[]) => {
                return sces.map((sce) => {
                    sce.module = SCE_MODULES.misspelling;
                    sce.label = sce.title;
                    return sce;
                });
            },
            () => {
                return [];
            },
        );
    }

    private getLanguageMatchSCE (): Promise<any> {
        const params = {
            page_size: 0,
        };
        return this.sourceCodeExcludesApiService.getAll(SCE_MODULES.language_match, params).then(
            (sces: any[]) => {
                return sces.map((sce) => {
                    sce.module = SCE_MODULES.language_match;
                    return sce;
                });
            },
            () => {
                return [];
            },
        );
    }

    private createGuidelineOpts (isOldGuideline: boolean, isMobileGuideline: boolean): any[] {
        const res: any[] = [];

        for (const [guideline, info] of this.guidelineMapEntries) {
            const isWCAG22Mobile = guideline.includes('WCAG22') && guideline.includes('mobile');

            if (guideline.includes('mobile') && !isMobileGuideline) {
                continue;
            }
            if (isOldGuideline && isWCAG22Mobile) {
                continue;
            }
            res.push(this.createGuidelineOption(guideline, info.name));
        }
        return res;
    }

    private createGuidelineOption (id: string, name: string): any {
        return {
            accessibility_guideline_id: id,
            name: name,
        };
    }
}
